import type { ActiveOrder, OrderCommand, TimedEvent, DialogueLine, StateUpdates, GameIntent } from "../types/game";

export interface FiredEvent {
  dialogue?: DialogueLine[];
  intents?: GameIntent[];
  stateUpdates?: StateUpdates;
}

export class TimerSystem {
  private _orders: Map<string, ActiveOrder> = new Map();
  private timedEvents: (TimedEvent & { fired: boolean; baseTime: number })[] = [];
  private elapsed = 0;
  private onOrderComplete: ((orderId: string, label: string) => void) | null = null;
  private onEventFired: ((event: FiredEvent) => void) | null = null;

  get orders(): ActiveOrder[] {
    return Array.from(this._orders.values());
  }

  setOnOrderComplete(cb: (orderId: string, label: string) => void) {
    this.onOrderComplete = cb;
  }

  setOnEventFired(cb: (event: FiredEvent) => void) {
    this.onEventFired = cb;
  }

  /** Process order commands from AI response */
  processOrders(commands: OrderCommand[]) {
    for (const cmd of commands) {
      switch (cmd.action) {
        case "add": {
          // Skip if an executing order with the same label already exists
          const label = cmd.label || cmd.id;
          const hasDuplicate = Array.from(this._orders.values()).some(
            o => o.status === "executing" && o.label === label
          );
          if (hasDuplicate) break;
          this._orders.set(cmd.id, {
            id: cmd.id,
            label,
            totalDuration: cmd.duration || 10,
            startedAt: Date.now(),
            status: "executing",
          });
          break;
        }

        case "update":
          const existing = this._orders.get(cmd.id);
          if (existing) {
            if (cmd.label) existing.label = cmd.label;
            if (cmd.remainingSeconds !== undefined) {
              existing.totalDuration = cmd.remainingSeconds;
              existing.startedAt = Date.now();
            }
          }
          break;

        case "complete": {
          const order = this._orders.get(cmd.id);
          if (order) {
            order.status = "complete";
            // Auto-remove after brief display
            setTimeout(() => this._orders.delete(cmd.id), 3000);
          }
          break;
        }

        case "cancel": {
          const order = this._orders.get(cmd.id);
          if (order) {
            order.status = "cancelled";
            setTimeout(() => this._orders.delete(cmd.id), 2000);
          }
          break;
        }
      }
    }
  }

  /** Schedule timed events from AI response */
  scheduleEvents(events: TimedEvent[]) {
    const baseTime = this.elapsed;
    for (const event of events) {
      this.timedEvents.push({
        ...event,
        fired: false,
        baseTime,
      });
    }
  }

  /** Called every frame or at regular intervals */
  tick(deltaSeconds: number) {
    this.elapsed += deltaSeconds;

    // Check timed events
    for (const event of this.timedEvents) {
      if (!event.fired && this.elapsed >= event.baseTime + event.triggerAtSeconds) {
        event.fired = true;
        this.onEventFired?.({
          dialogue: event.dialogue,
          intents: event.intents,
          stateUpdates: event.stateUpdates,
        });
      }
    }

    // Clean up old fired events
    this.timedEvents = this.timedEvents.filter((e) => !e.fired);

    // Check order completions
    const now = Date.now();
    for (const [id, order] of this._orders) {
      if (order.status !== "executing") continue;
      const elapsed = (now - order.startedAt) / 1000;
      if (elapsed >= order.totalDuration) {
        order.status = "complete";
        this.onOrderComplete?.(id, order.label);
        setTimeout(() => this._orders.delete(id), 3000);
      }
    }
  }

  cancelOrder(orderId: string) {
    const order = this._orders.get(orderId);
    if (order && order.status === "executing") {
      order.status = "cancelled";
      setTimeout(() => this._orders.delete(orderId), 2000);
    }
  }

  /** Cancel all executing orders (e.g. when all_stop is issued) */
  cancelAllOrders() {
    for (const [, order] of this._orders) {
      if (order.status === "executing") {
        order.status = "cancelled";
      }
    }
    // Clean up after brief display
    setTimeout(() => {
      for (const [id, order] of this._orders) {
        if (order.status === "cancelled") this._orders.delete(id);
      }
    }, 2000);
  }
}
